import { Controller } from "@hotwired/stimulus"

// Boczne kolumny widoku operatora czata (nowy widok):
// - lista rozmów (target "list")
// - karta klienta (target "clientPanel")
//
// Dwa tryby zależnie od szerokości ekranu:
// 1) DESKTOP (kolumny widoczne): przyciski "listBtn"/"clientBtn" zwijają/rozwijają kolumnę
//    klasą `!hidden`; stan utrwalany w localStorage. Kolumna klienta - tylko xl+, lista - md+.
// 2) MOBILE/TABLET (kolumna ukryta): ta sama sekcja działa jako WYSUWANY OVERLAY (drawer) -
//    lista z lewej (poniżej md), karta klienta z prawej (poniżej xl). Slide realizujemy
//    techniką "mount-animate": najpierw zdejmujemy `hidden` (element w DOM, wciąż off-canvas),
//    wymuszamy reflow, potem zdejmujemy translate -> płynne wjechanie. Dzięki `hidden` w stanie
//    zamkniętym element nie generuje poziomego scrolla (display:none nie rozpycha dokumentu).
export default class extends Controller {
  static targets = ["list", "clientPanel", "listBtn", "clientBtn", "listBackdrop", "clientBackdrop"]

  connect() {
    // zwijanie kolumn dotyczy tylko trybu desktopowego (kolumny w flow) - na mobile to overlaye
    if (this._isMd()) {
      this._apply("list", localStorage.getItem("chatColList") === "closed")
    }
    if (this._isXl()) {
      this._apply("client", localStorage.getItem("chatColClient") === "closed")
    }
  }

  _isMd() { return window.matchMedia("(min-width: 768px)").matches }
  _isXl() { return window.matchMedia("(min-width: 1280px)").matches }

  // --- desktop: zwijanie kolumn ---
  toggleList() { this._toggle("list") }
  toggleClient() { this._toggle("client") }

  // --- mobile/tablet: wysuwane overlaye (drawer ze slide) ---
  toggleListOverlay() {
    if (!this.hasListTarget) { return }
    this.listTarget.classList.contains("hidden") ? this.openListOverlay() : this.closeListOverlay()
  }

  openListOverlay() { this._openOverlay(this._listPanel(), this._listBackdrop(), "-translate-x-full") }
  closeListOverlay() { this._closeOverlay(this._listPanel(), this._listBackdrop(), "-translate-x-full") }

  toggleClientOverlay() {
    if (!this.hasClientPanelTarget) { return }
    this.clientPanelTarget.classList.contains("hidden") ? this.openClientOverlay() : this.closeClientOverlay()
  }

  openClientOverlay() { this._openOverlay(this._clientPanel(), this._clientBackdrop(), "translate-x-full") }
  closeClientOverlay() { this._closeOverlay(this._clientPanel(), this._clientBackdrop(), "translate-x-full") }

  _listPanel() { return this.hasListTarget ? this.listTarget : null }
  _clientPanel() { return this.hasClientPanelTarget ? this.clientPanelTarget : null }
  _listBackdrop() { return this.hasListBackdropTarget ? this.listBackdropTarget : null }
  _clientBackdrop() { return this.hasClientBackdropTarget ? this.clientBackdropTarget : null }

  // pokazanie: mount (zdejmij hidden) -> reflow -> wjazd (zdejmij translate off-canvas)
  _openOverlay(panel, backdrop, offClass) {
    if (!panel) { return }
    panel.classList.remove("hidden")
    panel.classList.add("flex") // baza ma tylko md:flex/xl:flex - na mobile wymuszamy flex
    if (backdrop) { backdrop.classList.remove("hidden") }
    panel.getBoundingClientRect() // wymuś reflow, żeby transform startował z pozycji off-canvas
    panel.classList.remove(offClass)
    panel.classList.add("translate-x-0")
    document.documentElement.classList.add("overflow-hidden") // blokada scrolla tła pod drawerem
  }

  // schowanie: wyjazd (dodaj translate) -> po zakończeniu transition ukryj (hidden)
  _closeOverlay(panel, backdrop, offClass) {
    if (!panel) { return }
    panel.classList.add(offClass)
    panel.classList.remove("translate-x-0")
    if (backdrop) { backdrop.classList.add("hidden") }
    document.documentElement.classList.remove("overflow-hidden")
    const onEnd = (e) => {
      if (e.target !== panel) { return }
      panel.classList.add("hidden")
      panel.classList.remove("flex")
      panel.removeEventListener("transitionend", onEnd)
    }
    panel.addEventListener("transitionend", onEnd)
  }

  _toggle(which) {
    const key = which === "list" ? "chatColList" : "chatColClient"
    const closed = localStorage.getItem(key) !== "closed"
    localStorage.setItem(key, closed ? "closed" : "open")
    this._apply(which, closed)
  }

  _apply(which, closed) {
    const panel = which === "list" ? this._listPanel() : this._clientPanel()
    const btn = which === "list"
      ? (this.hasListBtnTarget ? this.listBtnTarget : null)
      : (this.hasClientBtnTarget ? this.clientBtnTarget : null)

    if (panel) panel.classList.toggle("!hidden", closed)
    if (btn) {
      btn.classList.toggle("bg-blue-50", !closed)
      btn.classList.toggle("text-blue-600", !closed)
      btn.classList.toggle("text-gray-500", closed)
    }
  }
}
